// Import React.
import React from "react";

// Import the login page CSS module.
import styles from "../LoginPage.module.css";



// Store the login-page feature groups.
const featureGroups = [

    // Features covering the different ways users can sell.
    {
        // Give the feature group a stable identifier.
        id: "sell",

        // Set the group heading.
        title: "Sell your way",

        // Set the icon representing this group.
        icon: "store",

        // Define the features within the group.
        features: [
            "Unlimited free stores",
            "Pop up product links",
            "Inline shopping cart",
            "Manual order creation",
            "API integration",
        ],
    },

    // Features covering product creation.
    {
        // Give the feature group a stable identifier.
        id: "create",

        // Set the group heading.
        title: "Create products",

        // Set the icon representing this group.
        icon: "design",

        // Define the features within the group.
        features: [
            "Print-ready AI image generation",
            "Large product range",
            "One design - multiple products",
            "Free mockup generation",
            //"Image storage and management",
        ],
    },

    // Features covering order fulfilment.
    {
        // Give the feature group a stable identifier.
        id: "fulfil",

        // Set the group heading.
        title: "We handle fulfilment",

        // Set the icon representing this group.
        icon: "shipping",

        // Define the features within the group.
        features: [
            "Automatic order fulfilment",
            "We handle packing and shipping",
            "We handle customs and duties",
            "We handle customer support",
            "Fully tracked shipping",
            "Automated progress emails",
            //"Multiple shipping methods",
            //"International shipping",
            //"Order tracking and management",
            //"Customer confirmation and tracking emails",
        ],
    },

    // Features covering pricing and earnings.
    {
        // Give the feature group a stable identifier.
        id: "earnings",

        // Set the group heading.
        title: "Pricing & earnings",

        // Set the icon representing this group.
        icon: "money",

        // Define the features within the group.
        features: [

            "Automated pricing rules",
            "Minimum profit calculator",
            "Fees, taxes and shipping included",
            "Automatic currency conversion",
            "Transparent, up-front costing",
            /*
            "Automatic store price calculations",
            "Fees, taxes and shipping included",
            "Automatic currency conversion",
            "Automatic billing",
            "Request earnings after 30 days",
            "Reduced fulfilment costs with subscription plans",
            */
        ],
    },

    /*
    // Features covering post-sale support.
    {
        // Give the feature group a stable identifier.
        id: "support",

        // Set the group heading.
        title: "Post-sale support",

        // Set the icon representing this group.
        icon: "support",

        // Define the features within the group.
        features: [
            "Post-sale customer service",
            "Support on StrichSuite-managed orders",
        ],
    },
    */

];



// Render an icon based on the supplied icon type.
const renderFeatureIcon = (icon) => {

    // Render the store icon.
    if (icon === "store") {

        // Return a store SVG.
        return (

            // Render the decorative store icon.
            <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
            >

                {/* Draw the store roof. */}
                <path d="M4 10L6 4H18L20 10" />

                {/* Draw the store body. */}
                <path d="M5 10V20H19V10" />

                {/* Draw the store entrance. */}
                <path d="M9 20V14H15V20" />

            </svg>

        );

    }

    // Render the product creation icon.
    if (icon === "design") {

        // Return the T-shirt SVG.
        return (

            // Render the decorative T-shirt icon.
            <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
            >

                {/* Draw the complete T-shirt shape. */}
                <path d="M8 4L4 6L2.5 10L6 11.5V20H18V11.5L21.5 10L20 6L16 4C15.5 6 14 7 12 7C10 7 8.5 6 8 4Z" />

            </svg>

        );

    }

    // Render the shipping icon.
    if (icon === "shipping") {

        // Return the shipping SVG.
        return (

            // Render the decorative delivery icon.
            <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
            >

                {/* Draw the main delivery vehicle. */}
                <path d="M3 6H14V17H3V6Z" />

                {/* Draw the delivery vehicle cab. */}
                <path d="M14 10H18L21 13V17H14V10Z" />

                {/* Draw the rear wheel. */}
                <circle
                    cx="7"
                    cy="18"
                    r="2"
                />

                {/* Draw the front wheel. */}
                <circle
                    cx="18"
                    cy="18"
                    r="2"
                />

            </svg>

        );

    }

    // Render the money icon.
    if (icon === "money") {

        // Return the wallet SVG.
        return (

            // Render the decorative wallet icon.
            <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
            >

                {/* Draw the wallet body. */}
                <rect
                    x="3"
                    y="6"
                    width="18"
                    height="13"
                    rx="2"
                />

                {/* Draw the wallet opening. */}
                <path d="M15 10H21V15H15C13.5 15 12.5 14 12.5 12.5C12.5 11 13.5 10 15 10Z" />

                {/* Draw the wallet fastening. */}
                <circle
                    cx="16"
                    cy="12.5"
                    r="0.7"
                />

            </svg>

        );

    }

    // Return the default support icon.
    return (

        // Render the decorative support icon.
        <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
        >

            {/* Draw the headset band. */}
            <path d="M4 13V11C4 6.5 7.5 3 12 3C16.5 3 20 6.5 20 11V13" />

            {/* Draw the left headset earpiece. */}
            <path d="M4 12H7V18H4V12Z" />

            {/* Draw the right headset earpiece. */}
            <path d="M17 12H20V18H17V12Z" />

            {/* Draw the microphone arm. */}
            <path d="M20 17C20 20 18 21 14 21" />

        </svg>

    );

};



// Export the reusable feature panel.
export const LoginFeatures = () => {

    // Render the feature panel.
    return (

        // Constrain the content within the coloured section.
        <div className={styles.login_features}>

            {/* Render the feature introduction. */}
            <div className={styles.login_features__heading}>

                {/* Render the introductory eyebrow. 
                <span className={styles.login_features__eyebrow}>
                    Everything in one place
                </span>
                */}

                {/* Render the main feature heading. */}
                <h2 className={styles.login_features__title}>
                    You create. We handle the rest.
                </h2>

                {/* Render the supporting feature copy. */}
                <p className={styles.login_features__subtitle}>
                    StrichSuite brings designing, selling, payment handling, delivery, customs handling and customer support together in one platform.
                </p>

            </div>

            {/* Render the feature groups. */}
            <div className={styles.login_features__groups}>

                {/* Loop through each feature group. */}
                {featureGroups.map((featureGroup) => (

                    // Render the individual feature group.
                    <section
                        className={styles.login_features__group}
                        key={featureGroup.id}
                    >

                        {/* Render the feature group heading row. */}
                        <div className={styles.login_features__group_heading}>

                            {/* Render the group icon. */}
                            <div className={styles.login_features__icon}>

                                {/* Render the selected icon. */}
                                {renderFeatureIcon(featureGroup.icon)}

                            </div>

                            {/* Render the group title. */}
                            <h3 className={styles.login_features__group_title}>
                                {featureGroup.title}
                            </h3>

                        </div>

                        {/* Render the group's features. */}
                        <ul className={styles.login_features__list}>

                            {/* Loop through the features in this group. */}
                            {featureGroup.features.map((feature) => (

                                // Render the individual feature.
                                <li
                                    className={styles.login_features__item}
                                    key={feature}
                                >

                                    {/* Render the feature check icon. */}
                                    <span
                                        className={styles.login_features__check}
                                        aria-hidden="true"
                                    >
                                        ✓
                                    </span>

                                    {/* Render the feature text. */}
                                    <span>
                                        {feature}
                                    </span>

                                </li>

                            ))}

                        </ul>

                    </section>

                ))}

            </div>

        </div>

    );

};